export type Pages = string;
/***
 * Define the pages and routing parameters needed.
 */
export const Pages = {
  HomePage: '/',
  Authorization: '/authorization',
  Contact: '/contact',
  ContactDetails: '/contact/:contactId',
  ContactList: '/contacts',
  MaintenanceList: '/maintenance',
  MaintenanceDetails: '/maintenance/:reservationId',
  MaintenanceSchedule: '/maintenanceSchedule',
  Oncall: '/oncall',
  SiteTimelines: '/siteTimelines',
  Site: '/site/:siteId',
  AntennaView: '/site/:siteId/antenna/:antennaId',
  AntennaViewTab: '/site/:siteId/antenna/:antennaId/:tabId?',
  DataExplorer: '/dataExplorer',
  DataPlaneDashboard: '/dataPlaneDashboard',
  SNAP: `/snap`,
  SNAPSite: '/snap/:siteId/:deviceId?',
  ViewCustomer: '/view-customer',
  ViewMultipleCustomers: '/view-multiple-customers',
  CustomerDetails: '/customer/:customerId',
  MissionProfileDetails: '/customer/:customerId/missionprofile/:missionProfileId',
  ConfigDetails: '/customer/:customerId/config/:configId',
  SatelliteDetails: '/customer/:customerId/satellite/:satelliteId',
  CustomerOnboardingValidator: '/customer-onboarding-validator',
  ListSatellites: '/satellites',
  SatelliteOnboarding: '/satellite-onboarding',
  CppaOnboarding: '/cppa-onboarding',
  UplinkRestrictions: '/uplink-restrictions',
  // Antenna Inventory. Note that InventoryPartDetail and InventorySiteDetail must be routed
  // before their list counterparts, since `/inventory/parts` prefix-matches
  // `/inventory/parts/:partId`.
  Inventory: '/inventory',
  InventoryParts: '/inventory/parts',
  InventoryPartDetail: '/inventory/parts/:partId',
  InventorySites: '/inventory/sites',
  InventorySiteDetail: '/inventory/sites/:siteCode',
  // A single physical unit, as distinct from the catalog part it is an instance of.
  InventoryInstanceDetail: '/inventory/instances/:instanceId',
  InventoryDirectory: '/inventory/directory',
  InventoryRma: '/inventory/rma',
  // A sub-page of the RMA Tracker rather than an eighth tab. Must be routed before
  // InventoryRma, which prefix-matches it.
  InventoryRmaAnalytics: '/inventory/rma/analytics',
  InventoryOrders: '/inventory/orders',
  InventorySpares: '/inventory/spares',
};

/**
 * Keeps our breadcrumbs for use elsewhere
 *
 * We have this as a class with only static methods because we can make them
 * getters and then will be re-evaluated on each access, which is important
 * for preserving the current location.search.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class BREADCRUMBS {
  public static get Home() {
    return {
      text: 'Home',
      href: Pages.HomePage,
    };
  }

  public static get Oncall() {
    return {
      text: 'Oncall',
      href: Pages.Oncall,
    };
  }

  public static get Authorization() {
    return {
      text: 'Authorization',
      href: Pages.Authorization,
    };
  }

  public static get ListContacts() {
    return {
      text: 'List Contacts',
      href: Pages.ContactList,
    };
  }

  public static get MaintenanceList() {
    return {
      text: 'List Maintenance',
      href: Pages.MaintenanceList,
    };
  }

  public static MaintenanceDetails(reservationId: string) {
    return {
      text: reservationId,
      href: Pages.MaintenanceDetails,
    };
  }

  public static get MaintenanceSchedule() {
    return {
      text: 'Schedule Maintenance',
      href: Pages.MaintenanceSchedule + location.search,
    };
  }

  public static get SNAP() {
    return {
      text: 'Antenna Portal (SNAP)',
      href: Pages.SNAP,
    };
  }

  public static SNAPSite(siteId: string, deviceId: string | undefined) {
    const snapSite = [
      {
        text: siteId,
        href: `${Pages.SNAP}/${siteId}`,
      },
    ];
    if (deviceId !== undefined && deviceId !== '') {
      snapSite.push({
        text: deviceId,
        href: `${Pages.SNAP}/${siteId}/${deviceId}`,
      });
    }
    return snapSite;
  }

  public static get SiteTimelines() {
    return {
      text: 'Site Timelines',
      href: Pages.SiteTimelines,
    };
  }

  public static Site(siteId: string) {
    return {
      text: siteId,
      href: Pages.Site.replace(':siteId', siteId),
    };
  }

  public static AntennaView(siteId: string, antennaId: string) {
    return {
      text: antennaId,
      href: Pages.AntennaView.replace(':siteId', siteId).replace(':antennaId', antennaId),
    };
  }

  public static get ContactSearch() {
    return {
      text: 'View Individual Contact',
      href: Pages.Contact,
    };
  }

  public static ContactDetails(contactId: string) {
    return {
      text: contactId,
      href: Pages.Contact,
    };
  }

  public static get DataExplorer() {
    return {
      text: 'Data Explorer',
      href: Pages.DataExplorer,
    };
  }

  public static get DataPlaneDashboard() {
    return {
      text: 'Data Plane Dashboard',
      href: Pages.DataPlaneDashboard,
    };
  }

  public static get ViewCustomer() {
    return {
      text: 'View Customer',
      href: Pages.ViewCustomer,
    };
  }

  public static get ViewMultipleCustomers() {
    return {
      text: 'View Multiple Customers',
      href: Pages.ViewMultipleCustomers,
    };
  }

  public static get CustomerOnboardingValidator() {
    return {
      text: 'Customer Onboarding Validator',
      href: Pages.CustomerOnboardingValidator,
    };
  }

  public static get ListSatellites() {
    return {
      text: 'List Satellites',
      href: Pages.ListSatellites,
    };
  }

  public static get SatelliteOnboarding() {
    return {
      text: 'Satellite Onboarding',
      href: Pages.SatelliteOnboarding,
    };
  }

  public static get CppaOnboarding() {
    return {
      text: 'CPPA Onboarding',
      href: Pages.CppaOnboarding,
    };
  }

  public static get UplinkRestrictions() {
    return {
      text: 'Uplink Restrictions',
      href: Pages.UplinkRestrictions,
    };
  }

  public static CustomerDetails(customerId: string) {
    return {
      text: customerId,
      href: Pages.CustomerDetails.replace(':customerId', customerId),
    };
  }

  public static MissionProfileDetails(customerId: string, missionProfileId: string) {
    return {
      text: missionProfileId,
      href: Pages.MissionProfileDetails.replace(':customerId', customerId).replace(
        ':missionProfileId',
        missionProfileId,
      ),
    };
  }

  public static ConfigDetails(customerId: string, configId: string) {
    return {
      text: configId,
      href: Pages.ConfigDetails.replace(':customerId', customerId).replace(':configId', configId),
    };
  }

  public static SatelliteDetails(customerId: string, satelliteId: string) {
    return {
      text: satelliteId,
      href: Pages.SatelliteDetails.replace(':customerId', customerId).replace(':satelliteId', satelliteId),
    };
  }

  public static get Inventory() {
    return {
      text: 'Inventory',
      href: Pages.Inventory,
    };
  }

  public static get InventoryParts() {
    return {
      text: 'Parts Catalog',
      href: Pages.InventoryParts,
    };
  }

  public static get InventorySites() {
    return {
      text: 'Site Inventory',
      href: Pages.InventorySites,
    };
  }

  public static get InventoryDirectory() {
    return {
      text: 'Site Directory',
      href: Pages.InventoryDirectory,
    };
  }

  public static get InventoryRma() {
    return {
      text: 'RMA Tracker',
      href: Pages.InventoryRma,
    };
  }

  public static get InventoryRmaAnalytics() {
    return {
      text: 'Analytics',
      href: Pages.InventoryRmaAnalytics,
    };
  }

  public static get InventoryOrders() {
    return {
      text: 'Purchase Orders',
      href: Pages.InventoryOrders,
    };
  }

  public static get InventorySpares() {
    return {
      text: 'Spares Reconciliation',
      href: Pages.InventorySpares,
    };
  }

  /**
   * Breadcrumb for a single part.
   *
   * `partId` is a ULID and is never shown to users, so the label is the part's display number
   * resolved by the caller via `formatPartNumber`. The href still uses the ULID.
   */
  public static InventoryPartDetail(partId: string, label: string) {
    return {
      text: label,
      href: Pages.InventoryPartDetail.replace(':partId', partId),
    };
  }

  public static InventorySiteDetail(siteCode: string) {
    return {
      text: siteCode,
      href: Pages.InventorySiteDetail.replace(':siteCode', siteCode),
    };
  }

  /**
   * Breadcrumb for a single unit.
   *
   * `instanceId` is a ULID and never shown, so the caller supplies a label — a serial number where
   * there is one, and something human otherwise, since roughly two in five units have no serial.
   */
  public static InventoryInstanceDetail(instanceId: string, label: string) {
    return {
      text: label,
      href: Pages.InventoryInstanceDetail.replace(':instanceId', instanceId),
    };
  }
}
